import { debounce } from 'lodash';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { TextField } from 'Containers/App/types';
import { useTextContext } from 'Contexts/textContext';
import { Paragraph } from 'DesignLibrary/atoms';
import TextInput from 'DesignLibrary/atoms/inputs/TextInput';
import { AriaHiddenAlert } from 'Utils/accessibility';

import { Wrapper } from './styled';
import { Suggestion, SuggestionsList } from './SuggestionsList';

interface TypeaheadSearchProps {
  isLoading: boolean;
  suggestions: Suggestion[];
  placeholder: string;
  isDisabled: boolean;
  isMemberComplete: boolean;
  labelId: string;
  searchKey: string;
  noResultsField: TextField;
  queryResults: (value: string) => void;
  handleSelectResult: (key: string, title: string, description: string) => void;
  errorTextField?: TextField | undefined;
}

const TypeaheadSearch = ({
  isLoading,
  suggestions,
  placeholder,
  isDisabled,
  isMemberComplete,
  labelId,
  searchKey,
  noResultsField,
  handleSelectResult,
  queryResults,
  errorTextField,
}: TypeaheadSearchProps) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState({
    userInput: '',
    showSuggestions: false,
    activeSuggestion: 0,
  });
  const [hiddenAlert, setHiddenAlert] = useState<string | null>(null);
  const [showLoading, setShowLoading] = useState<boolean>(false);

  const { retrieveContentfulData } = useTextContext();

  useEffect(() => {
    setShowLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    // focus on search bar on first DidMount
    if (!isMemberComplete && searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (suggestions.length > 0) {
      const numberOfResultsAlert = retrieveContentfulData<string>(
        'profile_section.member.accessibility.drug_search_results_alert',
      );
      setHiddenAlert(numberOfResultsAlert.replace(/{x}/, suggestions.length.toString()));
    }
  }, [suggestions]);

  const onFocus = () => {
    setState({ ...state, showSuggestions: true });
  };

  const onBlur = () => {
    setState({ ...state, showSuggestions: false });
  };

  const onChange = (e) => {
    setState({
      activeSuggestion: 0,
      showSuggestions: true,
      userInput: e.target.value,
    });

    if (e.target.value.length > 2) {
      setShowLoading(true);
      typeaheadSearchThrottled(e.target.value);
    }
  };

  const onResultClick = (e, idx) => {
    const key = suggestions[idx].key;
    const title = suggestions[idx].title;
    const description = suggestions[idx].description;

    setState({
      activeSuggestion: 0,
      showSuggestions: false,
      userInput: '',
    });

    // set focus back to typeahead search
    if (searchRef.current) {
      searchRef.current.focus();
    }

    handleSelectResult(key, title, description);
  };

  const onKeyDown = (e) => {
    const { activeSuggestion, showSuggestions } = state;
    // Only allow suggestion selection when showSuggestions is true and we have actual search results to show
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'Enter') {
        // Set selected result on reducer
        const key = suggestions[activeSuggestion].key;
        const title = suggestions[activeSuggestion].title;
        const description = suggestions[activeSuggestion].description;
        handleSelectResult(key, title, description);

        // When user selects a result, reset active suggestions and update selection
        setState({
          activeSuggestion: 0,
          showSuggestions: false,
          userInput: '',
        });
      } else if (e.key === 'ArrowUp') {
        // User presses up arrow and no activeSuggestion or first index is the activeSuggestion do not drecrement active index
        if (activeSuggestion <= 0) {
          return;
        }
        // User presses up arrow, decrement active index
        setState({ ...state, activeSuggestion: activeSuggestion - 1 });
      } else if (e.key === 'ArrowDown') {
        // User presses down arrow and no activeSuggestion or last index is the activeSuggestion do not increment active index
        if (activeSuggestion + 1 === suggestions.length) {
          return;
        }
        // User presses down arrow, increment active index
        setState({ ...state, activeSuggestion: activeSuggestion + 1 });
      }
    }
  };

  const typeaheadSearchThrottled = useCallback(
    debounce((value) => queryResults(value), 500),
    [],
  );

  const { showSuggestions, userInput, activeSuggestion } = state;

  const suggestionsListVisible = showSuggestions && userInput;

  // Show error if user has typed, not selected anything,
  // closed the suggestions list, and we have text to display
  const showError =
    !!userInput && !activeSuggestion && !suggestionsListVisible && errorTextField !== undefined;
  const errorText = errorTextField ? retrieveContentfulData<string>(errorTextField) : '';

  return (
    <Wrapper>
      <TextInput
        role="combobox"
        aria-labelledby={labelId}
        aria-autocomplete="none"
        aria-expanded={Boolean(suggestionsListVisible)}
        aria-controls={`${searchKey}-suggestions`}
        aria-activedescendant={
          suggestionsListVisible
            ? `${searchKey}-suggestion-${activeSuggestion}`
            : '' /* activedescendant being set only makes sense when the combobox has focus and the suggestion list is visible */
        }
        ref={searchRef}
        // className="typeahead-search-input"
        data-testid={`${searchKey}-search-input`}
        inputMode="text"
        handleChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        value={state.userInput}
        placeholder={placeholder}
        disabled={isDisabled}
        inputType="icon"
        iconType="Search"
        error={showError}
        stretch
      />
      {showError && errorText && (
        <Paragraph size="mini" color="--primary-red" data-testid="typeahead-error">
          {errorText}
        </Paragraph>
      )}
      <AriaHiddenAlert>{hiddenAlert}</AriaHiddenAlert>
      {suggestionsListVisible && (
        <SuggestionsList
          suggestionKey={searchKey}
          noResultsField={noResultsField}
          showLoading={showLoading}
          userInput={userInput}
          suggestions={suggestions}
          activeSuggestion={activeSuggestion}
          onSuggestionClick={onResultClick}
        />
      )}
    </Wrapper>
  );
};

export default TypeaheadSearch;
